import { formatPrice } from '../utils/formatPrice';
import { useState } from 'react';
import { Heart, ShoppingCart, FileText, Plus, Minus, Eye } from 'lucide-react';
import { Product } from '../data/products';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';

interface Props {
  product: Product;
  compact?: boolean;
}

export default function ProductCard({ product, compact = false }: Props) {
  const { lang, addToCart, toggleFavorite, favorites, setPage, setSelectedProductId } = useApp();
  const T = t[lang];
  const [qty, setQty] = useState(product.moq || 1);
  const [added, setAdded] = useState(false);

  const isQuote = qty >= product.quoteThreshold;
  const isFav = favorites.includes(product.id);

  const stockLabel = () => {
    if (product.stock === 0) return T.bestsellers.outStock;
    if (product.stock < 20) return T.bestsellers.lowStock;
    return T.bestsellers.inStock;
  };

  const stockColor = () => {
    if (product.stock === 0) return 'text-red-600 bg-red-50';
    if (product.stock < 20) return 'text-amber-600 bg-amber-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  const handleAction = () => {
    if (isQuote) {
      addToCart({ product, qty });
      setPage('quote-request');
    } else {
      addToCart({ product, qty });
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const openProduct = () => {
    window.location.href = '/product/' + product.id;
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-gray-200 transition-all duration-200 flex flex-col group">
      <div className="relative overflow-hidden rounded-t-xl aspect-[4/3] bg-gray-50 flex items-center justify-center">
        {product.images && product.images.length > 0 ? (
          <img
            src={product.images[0]}
            alt={lang === 'fr' ? product.name : product.nameEn}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300 absolute inset-0"
          />
        ) : (
          <img
            src="/logo-1762256791_-_Modifie.png"
            alt="Lodiss"
            className="w-1/2 opacity-20 grayscale object-contain group-hover:scale-[1.02] transition-transform duration-300"
          />
        )}
        {(product.badge || product.badgeEn) && (
          <span className="absolute top-2 left-2 bg-lodiss-red text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide">
            {lang === 'fr' ? product.badge : product.badgeEn}
          </span>
        )}
        <button
          onClick={() => toggleFavorite(product.id)}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-150 ${
            isFav ? 'bg-red-50 text-lodiss-red' : 'bg-white/80 text-gray-400 hover:text-lodiss-red hover:bg-red-50'
          }`}
        >
          <Heart size={14} fill={isFav ? 'currentColor' : 'none'} />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="mb-1">
          <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium">{product.brand}</p>
          <h3 className="text-sm font-semibold text-gray-900 mt-0.5 leading-tight truncate group-hover:text-lodiss-red transition-colors duration-150">
            {lang === 'fr' ? product.name : product.nameEn}
          </h3>
        </div>

        <div className="flex items-center gap-2 mt-2 mb-3">
          {product.norm.slice(0, 1).map(n => (
            <span key={n} className="text-[10px] text-gray-400 border border-gray-200 px-1.5 py-0.5 rounded font-mono">
              {n}
            </span>
          ))}
        </div>

        <div className="mt-auto">
          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-lg font-bold text-gray-900">{formatPrice(product.price)}</span>
            <span className="text-xs text-gray-400">/ {lang === 'fr' ? 'unité' : 'unit'}</span>
          </div>

          {!compact && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQty(Math.max(product.moq || 1, qty - 1))}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="w-10 text-center text-sm font-semibold text-gray-900">{qty}</span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                {isQuote && (
                  <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded font-medium">
                    {T.common.quoteFrom} {product.quoteThreshold}
                  </span>
                )}
              </div>

              {isQuote && (
                <p className="text-[11px] text-amber-700 mb-2 leading-snug">{T.common.quoteVolMsg}</p>
              )}
            </>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleAction}
              disabled={product.stock === 0}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                product.stock === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isQuote
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : added
                  ? 'bg-emerald-600 text-white'
                  : 'bg-lodiss-red hover:bg-lodiss-dark text-white'
              }`}
            >
              {isQuote ? (
                <><FileText size={12} /> {T.common.requestQuote}</>
              ) : added ? (
                <>{lang === 'fr' ? 'Ajouté !' : 'Added!'}</>
              ) : (
                <><ShoppingCart size={12} /> {T.common.addToCart}</>
              )}
            </button>
            <button
              onClick={openProduct}
              className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-500 hover:border-lodiss-red hover:text-lodiss-red transition-colors duration-150"
            >
              <Eye size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
